/* Attendance API client — Phase 5 Bundle A + C
 *
 * Bundle A: list, upsert (by admin), adaptByName for UI keying
 * Bundle C: clockIn, clockOut, markAbsentSelf (employee self-service)
 */
import { api } from "./api";

const BASE = "/attendance";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
export function monthLabelToYM(label) {
  if (!label) return "";
  const [monthName, year] = label.split(" ");
  const m = MONTH_NAMES.indexOf(monthName);
  if (m < 0 || !year) return "";
  return `${year}-${String(m + 1).padStart(2, "0")}`;
}

export function attendanceApiToUi(r) {
  return {
    id: r.id,
    employeeId: r.employeeId,
    date: r.date ? String(r.date).slice(0, 10) : "",
    status: r.status,
    note: r.note || "",
    clockInAt:  r.clockInAt  ?? null,
    clockOutAt: r.clockOutAt ?? null,
    clockInTime:  r.clockInAt  ? new Date(r.clockInAt).toLocaleTimeString("en-IN",  { hour: "2-digit", minute: "2-digit", hour12: false }) : null,
    clockOutTime: r.clockOutAt ? new Date(r.clockOutAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : null,
    // Bundle C — selfie URLs + locations
    clockInSelfie:    r.clockInSelfieUrl  || null,
    clockInSelfieKey: r.clockInSelfieKey  || null,
    clockInLocation:  r.clockInLocation   || "",
    clockOutSelfie:   r.clockOutSelfieUrl || null,
    clockOutSelfieKey: r.clockOutSelfieKey || null,
    clockOutLocation: r.clockOutLocation  || "",
    markedAsSelf: r.markedAsSelf,
    markedBy: r.markedAsSelf ? "self" : "admin",
    // The UI's old code reads .selfie + .location too for backward compat
    selfie:   r.clockInSelfieUrl || null,
    location: r.clockInLocation  || "",
  };
}

export function adaptByName(apiRows, employees) {
  const idToName = {};
  for (const e of employees) idToName[e.id] = e.name;

  const byName = {};
  for (const e of employees) byName[e.name] = [];

  for (const r of apiRows) {
    const name = idToName[r.employeeId] || r.employeeName || "—";
    if (!byName[name]) byName[name] = [];
    byName[name].push(attendanceApiToUi(r));
  }
  for (const name of Object.keys(byName)) {
    byName[name].sort((a, b) => a.date.localeCompare(b.date));
  }
  return byName;
}

export const attendanceApi = {
  async listByMonth(monthLabel) {
    const month = monthLabelToYM(monthLabel);
    if (!month) return [];
    const res = await api.get(`${BASE}?month=${encodeURIComponent(month)}`);
    return Array.isArray(res) ? res : (res.items || []);
  },

  async upsert({ employeeId, date, status, note, clockInAt, clockOutAt }) {
    const payload = { employeeId, date, status };
    if (note !== undefined) payload.note = note;
    if (clockInAt) payload.clockInAt = clockInAt;
    if (clockOutAt) payload.clockOutAt = clockOutAt;
    const res = await api.post(BASE, payload);
    return attendanceApiToUi(res);
  },

  async bulkUpsert(entries) {
    return api.post(`${BASE}/bulk`, { entries });
  },

  async delete(id) {
    return api.delete(`${BASE}/${encodeURIComponent(id)}`);
  },

  // ─── Phase 5 Bundle C — self-service clock-in / out / absent ───────

  /** Logged-in user clocks themselves in with a selfie. */
  async clockIn({ date, clockInAt, selfieDataUrl, location, note }) {
    const payload = { date, clockInAt, selfieDataUrl };
    if (location) payload.location = location;
    if (note)     payload.note = note;
    const res = await api.post(`${BASE}/clock-in`, payload);
    return attendanceApiToUi(res);
  },

  async clockOut({ date, clockOutAt, selfieDataUrl, location }) {
    const payload = { date, clockOutAt, selfieDataUrl };
    if (location) payload.location = location;
    const res = await api.post(`${BASE}/clock-out`, payload);
    return attendanceApiToUi(res);
  },

  async markAbsentSelf({ date, reason }) {
    const payload = { date };
    if (reason) payload.reason = reason;
    const res = await api.post(`${BASE}/mark-absent-self`, payload);
    return attendanceApiToUi(res);
  },
};
